import type { MembershipTier } from '@/lib/types';
import { eraseCookie, getCookie, setCookie } from '@/lib/cookies';

/**
 * Membership an agent link can hand a new sign-up, e.g. Storebrand Platinum gives Private Member and
 * Storebrand Private Banking gives Private Signature. 'free' means the link grants no tier, which is
 * how every link worked before (the agentId alone still gives member access).
 */
export const AGENT_LINK_TIERS: MembershipTier[] = ['free', 'private_member', 'private_signature', 'private_concierge'];

export function agentLinkTier(value: unknown): MembershipTier {
  return AGENT_LINK_TIERS.includes(value as MembershipTier) ? (value as MembershipTier) : 'free';
}

/**
 * A path on this site, or null. Used for where a link sends the visitor after sign-up or sign-in,
 * so a crafted `redirect_url` can never send anyone to another site ("//evil.com", "/\evil.com").
 * Whitespace is refused because browsers drop tabs and newlines, which turns "/\t/evil.com" into
 * "//evil.com". The length limit leaves room for the filtered /bookings queries sent to login.
 */
export function safeLandingPath(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const path = value.trim();
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  if (path.length > 2000 || /[\s\\]/.test(path)) return null;
  // Sign-in, sign-up and /join send people on themselves, so landing there would loop.
  if (/^\/(auth|join)(\/|\?|#|$)/.test(path)) return null;
  return path;
}

/**
 * Sign-in or sign-up, carrying where the visitor is on their way to and which agent link brought them,
 * so moving between the two pages loses neither.
 */
export function authPageHref(
  page: '/auth/login' | '/auth/signup',
  { redirectUrl, agentId }: { redirectUrl?: string | null; agentId?: string | null },
): string {
  const query = new URLSearchParams();
  if (agentId) query.set('agentId', agentId);
  if (redirectUrl) query.set('redirect_url', redirectUrl);
  const search = query.toString();
  return search ? `${page}?${search}` : page;
}

const LANDING_COOKIE = 'agentLanding';

/**
 * Remembers which page a /join visit asked for (its ?to=, else the link's landing page), tied to that
 * agent link. complete-profile and the email-verification page send the member there afterwards. A
 * cookie rather than sessionStorage, so it survives the verification link opening in a new tab; one
 * day, like the agentId cookie. A visit with no page clears whatever an earlier visit left.
 * Browser only.
 */
export function rememberLanding(agentId: string, path: string | null) {
  if (path) setCookie(LANDING_COOKIE, JSON.stringify({ a: agentId, p: path }), 1);
  else eraseCookie(LANDING_COOKIE);
}

/**
 * The page remembered for this agent link, or null. A member with no agent link (an existing member
 * who came through /join) passes null and gets whatever was remembered. Browser only.
 */
export function rememberedLanding(agentId: string | null | undefined): string | null {
  try {
    const stored = JSON.parse(getCookie(LANDING_COOKIE) || 'null') as { a?: string; p?: string } | null;
    if (!stored || (agentId && stored.a !== agentId)) return null;
    return safeLandingPath(stored.p);
  } catch {
    return null;
  }
}

/**
 * The address to give a partner: /join works for every agent link, old ones included, and is the only
 * one that also sends signed-in visitors to the landing page. Browser only (uses this site's origin).
 */
export function agentShareUrl(agentId: string, to?: string): string {
  const url = `${window.location.origin}/join/${agentId}`;
  if (!to) return url;
  // Plain paths stay readable (?to=/hotels); one with its own ?, & or # is encoded so it survives.
  return `${url}?to=${/[?&#]/.test(to) ? encodeURIComponent(to) : to}`;
}

/** Pages offered as ready-made partner links in admin, named as in the member menu. */
export const AGENT_SHARE_PAGES: { label: string; path: string }[] = [
  { label: 'Curated Experiences', path: '/initiatives' },
  { label: 'Hotel Booking Platform', path: '/hotels' },
  { label: 'Concierge Services', path: '/services' },
];
